/**
 * The main sidebar component that orchestrates search, details, and informational panels.
 *
 * The `SideBar` component acts as the central UI hub for property search and related modules.
 * It includes configurable sections for:
 * - A Welcome message (`HtmlSection`)
 * - A Property Search tool (`PropertySearchSection`)
 * - A Property Details viewer (`PropertyDetailsSection`)
 * - Optional static content sections (e.g., GIS Data Sales)
 * - A Useful Links list (`UsefulLinksSection`)
 *
 * The sidebar manages search state, coordinates map updates, and dynamically opens
 * the relevant accordion sections based on user interactions.
 */

import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import type { AppJsonConfig, SearchJsonType, SearchResult } from "../../types/config";
import useAccordionSlide from "../../utils/accordion/AccordionAnimation";
import useOneOpenAccordion from "../../utils/accordion/OneOpenAccordion";
import HtmlSection from "./HtmlSection";
import PropertySearchSection from "./PropertySearchSection";
import PropertyDetailsSection from "./PropertyDetailsSection";
import UsefulLinksSection from "./UsefulLinksSection";
import searchDef from "../../config/search.json";
import { searchParcels } from "../../utils/ArcgisSearch";
import { AccordionSectionHandle } from "../../utils/accordion/AccordionSection";
import { normalizeLayerUrl } from "../../utils/serviceRoot";
import { CalciteLoader } from "@esri/calcite-components-react";
import "./styles/SideBar.css";

type Props = {
  slot?: string;
  config: AppJsonConfig;
  onUpdateResults?: (rows: any[]) => void;
  onUpdateAttrs?: (attrs: Record<string, any>) => void;
  selectedAttrs?: Record<string, any>;
  layerAttrs?: Record<string, Record<number, any[]>>;
  onUpdateActiveRoot?: (root: string | null) => void;
};

const SideBar: React.FC<Props> = ({
  slot,
  config,
  onUpdateResults,
  onUpdateAttrs,
  selectedAttrs,
  layerAttrs = {},
  onUpdateActiveRoot,
}) => {
  useOneOpenAccordion();
  useAccordionSlide();

  const welcomeRef = useRef<AccordionSectionHandle>(null);
  const searchRef = useRef<AccordionSectionHandle>(null);
  const detailsRef = useRef<AccordionSectionHandle>(null);

  const searchTypes = useMemo(
    () => {
      const cfg = config as AppJsonConfig;
      return cfg.searchTypes && cfg.searchTypes.length ? cfg.searchTypes : searchDef.searchTypes ?? [];
    },
    [config]
  );

  const handlePropertySearch = useCallback(
    (type: SearchJsonType, formValues: Record<string, string>) => searchParcels(type, formValues, config.httpProxy),
    [config.httpProxy]
  );

  const [localAttrs, setLocalAttrs] = useState<Record<string, any>>({});
  const attrs = selectedAttrs && Object.keys(selectedAttrs).length ? selectedAttrs : localAttrs;

  useEffect(() => {
    if (selectedAttrs && Object.keys(selectedAttrs).length) {
      detailsRef.current?.open?.();
    }
  }, [selectedAttrs]);

  const layout = config.enabledModules?.sidebar?.propertyDetailsLayout ?? [];

  const [searching, setSearching] = useState(false);

  useEffect(() => {
    welcomeRef.current?.open?.();
  }, []);

  function coerceCenterAndGeom(feature: {
    raw?: { geometry?: any };
    attrs?: Record<string, any>;
  }): {
    center?: { latitude: number; longitude: number };
    geomJson?: any;
  } {
    const g = feature.raw?.geometry;
    if (g) return { geomJson: g };

    const a = feature.attrs || {};
    const latKey = ["LATITUDE", "Latitude", "lat", "POINT_Y", "Y"].find((k) => k in a);
    const lonKey = ["LONGITUDE", "Longitude", "lon", "POINT_X", "X"].find((k) => k in a);

    const lat = latKey ? Number(a[latKey]) : NaN;
    const lon = lonKey ? Number(a[lonKey]) : NaN;

    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      const center = { latitude: lat, longitude: lon };
      const geomJson = { x: lon, y: lat, spatialReference: { wkid: 4326 } };
      return { center, geomJson };
    }

    return {};
  }

  const defaultLayersRoot: string | undefined = config.layersLayer
    ? normalizeLayerUrl(config.layersLayer as any) || undefined
    : undefined;

  return (
    <>
      <aside slot={slot} className="psa-sidebar">
        <div className="psa-scroll">
          <HtmlSection title="Welcome" file={config.welcomePath} sectionRef={welcomeRef} />

          <PropertySearchSection
            sectionRef={searchRef}
            searchTypes={searchTypes as SearchJsonType[]}
            onSearch={async (st, vals) => {
              setSearching(true);
              try {
                const root = (st as any).queryLayer ? normalizeLayerUrl((st as any).queryLayer) : null;
                onUpdateActiveRoot?.(root);

                const res = await handlePropertySearch(st, vals);
                const rows = res.map((r) => {
                  const { center, geomJson } = coerceCenterAndGeom(r);
                  return {
                    ...(r.attrs ?? r.raw?.attributes ?? {}),
                    __center: center ?? r.center,
                    __geometry: geomJson ?? r.raw?.geometry ?? null,
                  };
                });

                onUpdateResults?.(rows);
                return res;
              } finally {
                setSearching(false);
              }
            }}
            onSelectResult={(r: SearchResult) => {
              const { center, geomJson } = coerceCenterAndGeom(r);
              const a = { ...(r.attrs ?? r.raw?.attributes ?? {}) };
              onUpdateAttrs?.({
                ...a,
                __center: center ?? r.center,
                __geometry: geomJson ?? r.raw?.geometry ?? null,
              });
              detailsRef.current?.open?.();
            }}
          />

          <PropertyDetailsSection
            sectionRef={detailsRef}
            layout={layout}
            attrs={attrs}
            layerAttrs={layerAttrs}
            defaultLayersRoot={defaultLayersRoot}
            layersMultiLayer={(config as any).layersMultiLayer ?? []}
          />

          <UsefulLinksSection links={config.usefulLinks} />
        </div>

        <div className="psa-fineprint">
          © {new Date().getFullYear()} {config.owner || "County"}
          <br />
          Hosted by GEOPowered | GEO Jobe GIS Consulting
        </div>
      </aside>

      {searching &&
        createPortal(
          <div className="app-loader-overlay" role="status" aria-live="polite">
            <CalciteLoader label="Loading search results" scale="m" />
          </div>,
          document.body
        )}
    </>
  );
};

export default SideBar;
